<script>
/**
 * @Author: zhangxiaojian
 * @Date: 2025/6/25
 * @LastEditors: zhangxiaojian
 * @Description 知识问答对话主界面
 **/

import Content from '@comp/KnowledgeQARenderer/components/Content.vue'
import { AIDify } from '@apis'
export default {
  name: 'Knowledge',
  props: {
    // 知识类型 0--知识问答，1--运维助手
    knowledgeType: {
      type: Number,
      default: 0
    },
    list: {
      // 历史记录列表
      type: Array,
      default: () => []
    }
  },
  emits: [
    /**
     * 消息对话内容更新事件
     * @param {Array} list 对话项列表
     */
    'messageUpdate'
  ],
  data() {
    return {
      messageList: [], // 对话列表
      conversationControl: null, // 请求控制器
      messageId: '', // 最后一条消息的ID
      isAnswering: false // 是否正在进行回答
    }
  },
  components: {
    Content
  },
  mounted() {
    // 显示历史记录
    this.messageList = this.list
  },
  methods: {
    /**
     * 清空会话
     */
    clearConversation() {
      this.messageList = []
    },
    /**
     * 重新加载回答
     * @param {object} item 对话项
     * @param {number} index 对话项索引
     */
    reloadAnswer(item, index) {
      if (this.isAnswering) {
        // 正在回答过程中，禁止重新加载其他回答
        return
      }
      let content = this.messageList[index - 1].content
      this.sendMessage(content, this.messageList[index])
    },
    /**
     * 停止回答
     */
    stopAnswer() {
      if (this.conversationControl) {
        // 取消请求
        this.conversationControl.abort()
        // 设置回答状态为false
        this.isAnswering = false
        let lastMessage = this.messageList[this.messageList.length - 1]
        lastMessage.content = '已停止生成'
        lastMessage.isDone = true
        this.$emit('messageUpdate', this.messageList)
      }
    },
    // HTML特殊字符转义
    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '/': '&#x2F;'
      }
      return text.replace(/[&<>"'\/]/g, (match) => map[match])
    },
    /**
     * 发送消息
     * @param {string} msg 用户发送的消息
     * @param {object} answerObj 回答项
     * @returns {Promise<void>}
     */
    async sendMessage(msg, answerObj) {
      this.isAnswering = true
      // 开始回答时移除消息提示
      this.messageId = ''
      //是否是新的会话
      let isNewAnswer = true
      if (!answerObj) {
        let requestId = Date.now()
        let answerId = Date.now() + 1
        answerObj = {
          isUser: false,
          id: answerId,
          isDone: false, // 是否回答完成
          content: '',
          articles: [] // 引用的文章列表
        }
        this.messageList.push(
          {
            isUser: true,
            id: requestId,
            content: this.escapeHtml(msg)
          },
          answerObj
        )
        this.$emit('messageUpdate', this.messageList)
        this.$nextTick(() => {
          const container = this.$refs.knowledgeContent
          if (container) {
            container.scrollToBottom()
          }
        })
      } else {
        isNewAnswer = false
        answerObj.isDone = false
        answerObj.content = ''
        answerObj.articles = []
      }

      this.conversationControl = new AbortController()
      try {
        let data = {
          query: msg.trim()
        }
        if (this.knowledgeType === 1) {
          data.type = 1
        }
        const res = await AIDify.chatMessages({
          data,
          headers: {
            'Content-Type': 'application/json' // 设置请求头，告诉服务器我们将发送JSON数据
          },
          sourceEvent: true,
          responseType: 'stream',
          timeout: 120000,
          controller: this.conversationControl
        })
        // 处理流式数据
        const reader = res.body.getReader()

        let partialChunk = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            // 回答完毕
            if (!answerObj.content) {
              // 没有内容
              answerObj.content = '暂时无可返回内容'
            }
            answerObj.isDone = true
            this.isAnswering = false
            break
          }

          // 处理流数据（假设返回格式为SSE）
          const text = new TextDecoder().decode(value)
          const lines = (partialChunk + text).split('\n\n')
          partialChunk = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('data:') && !line.includes('[DONE]')) {
              const data = JSON.parse(line.replace('data:', ''))
              //const content = data.choices[0]?.delta?.content || ''
              answerObj.content += data.data.answer || ''
              //if (data.data.messageId) {
              this.messageId = data.data.messageId
              //  }
              if (data.data.articles?.length > 0) {
                answerObj.articles = data.data.articles
              }
              this.$nextTick(() => {
                const container = this.$refs.knowledgeContent
                // 回答最新的答案自动滚动到下方
                if (container && isNewAnswer) {
                  container.scrollToBottom()
                }
              })
              this.$emit('messageUpdate', this.messageList)
              // this.appendToLastAIMessage(content)
            }
          }
        }
      } catch (e) {
        answerObj.isDone = true
        this.isAnswering = false
        if (e.message === 'OFFLINE') {
          // 断网了
          answerObj.content = '请检查网络'
        } else if (e.message === 'TIMEOUT') {
          answerObj.content = '网络连接超时，请检查网络'
        }
        this.$emit('messageUpdate', this.messageList)
        console.log(e)
      }
    }
  }
}
</script>

<template>
  <div class="knowledge">
    <div class="knowledge-renderer-body">
      <Content
        ref="knowledgeContent"
        :knowledge-type="knowledgeType"
        :message-list="messageList"
        :is-answering="isAnswering"
        :message-id="messageId"
        @sendMessage="sendMessage"
        @reloadAnswer="reloadAnswer"
        @clearConversation="clearConversation"
        @stopAnswer="stopAnswer"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.knowledge {
  height: 0;
  flex: 2;
  &-renderer-body {
    height: 100%;
  }
}
</style>
